import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useUpdateWorkspace } from '@aibees/shared/workspace';
import { random } from 'lodash-es';
import { memo } from 'react';
import { useInviteMember } from '../../api/use-invite-member';
import { EMPTY_MEMBER_DATA } from '../../constants';
import { useUserManagementContext } from '../../context';
import { AddMoreMemberButton } from '../add-more-member-button';
import { AddMemberFormContent } from './add-member-form-content';
import { schema } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { AddMemberFormData, AddMemberFormProps as Props } from './types';
import { prepareAddMemberData } from './utils';

export const AddMemberForm = memo(({ className, onClose }: Props) => {
  const notify = useNotify();
  const updateWorkspace = useUpdateWorkspace();
  const { members } = useUserManagementContext();
  const api = useInviteMember();

  const handleSubmit = (data: AddMemberFormData) => {
    api.mutateAsync(prepareAddMemberData(data)).then((workspace) => {
      updateWorkspace(workspace);
      data.members?.map((member) => {
        notify.complete(
          'Invite member sent successfully',
          `${member.firstName} ${member.lastName}’s has been successfully added to the invitation.`,
          random(1, 100),
          {
            icon: notificationIconMap.UserManagement!,
          }
        );
      });
    });
  };

  return (
    <Styled.Wrapper
      className={classnames('AddMemberForm', className)}
      scheme={schema}
      loading={api.isLoading}
      defaultValues={{ members: [EMPTY_MEMBER_DATA] }}
      submitButtonProps={{
        children: 'Invite',
        size: 'small',
        variant: 'contained',
      }}
      resetAfterSubmit
      actions={[
        {
          children: 'Cancel',
          variant: 'outlined',
          size: 'small',
          onClick: () => onClose?.(),
        },
      ]}
      actionsSx={SX.action}
      actionsPrepend={
        members &&
        members.fields.length < 5 && (
          <AddMoreMemberButton
            loading={api.isLoading}
            onAppend={members?.append}
          />
        )
      }
      onSubmit={handleSubmit}
      noValidate
    >
      <AddMemberFormContent />
    </Styled.Wrapper>
  );
});

AddMemberForm.displayName = 'AddMemberForm';
